// Component Registry - Maps component names from page JSON to actual React components
//
// IMPORTANT: registry components are imported STATICALLY (matching
// kannada-prabha's registry.tsx). A dynamic() wrapper — even with SSR enabled —
// is a lazy module, so during SSR streaming every per-column <Suspense> boundary
// in RowRenderer suspended while the module resolved: the HTML shipped an empty
// fallback div per column, the real content followed as hidden out-of-order
// chunks + $RC swap scripts (45 pending boundaries on home), and the page
// painted as blank boxes that popped in one after another ("blink"), staying
// blank entirely without JS. Static imports render in document order instead.
//
// Only truly client-only widgets (browser-API/heavy-lib dependent: Highcharts,
// Recharts, d3-geo, iframe players) stay dynamic with ssr: false.
import { ComponentType } from 'react';
import dynamic from 'next/dynamic';

// Video cards (thumbnail+title shells, SSR-safe)
import BigVideonews from '@/components/component_unique/BigVideonews';
import { VerticalBigVideoNews } from '@/components/component_vertical/VerticalBigVideoNews';
import VerticalBigVideoNewsBlack from '@/components/component_vertical/VerticalBigVideoNewsBlack';
import { VerticalMediumVideoNews } from '@/components/component_vertical/VerticalMediumVideoNews';
import { VerticalMediumVideoHeadingNews } from '@/components/component_vertical/VerticalMediumVideoHeadingNews';
import VerticalBigVideoCategoryNews from '@/components/component_vertical/VerticalBigVideoCategoryNews';
import HorizontalBigVideoCategoryNews from '@/components/component_horizontal/HorizontalBigVideoCategoryNews';
import HorizontalBigVideoCategoryNewsBlack from '@/components/component_horizontal/HorizontalBigVideoCategoryNewsBlack';
import HorizontalVideoNews from '@/components/component_horizontal/HorizontalVideoNews';
import HorizontalSmallVideoRightNews from '@/components/component_horizontal/HorizontalSmallVideoRightNews';

// Form components
import Form from '@/components/component_form/Form';
import CompanyForm from '@/components/component_form/CompanyForm';
import EditProfileForm from '@/components/component_form/EditprofileForm';
import NewsPaperfeedbackForm from '@/components/component_form/NewsPaperfeedbackForm';
import LoginRegisterForm from '@/components/component_form/LoginRegisterForm';
import OTPForm from '@/components/component_form/OTPForm';
import ReadFullForm from '@/components/component_form/ReadFullForm';
import PollForm from '@/components/component_form/PollForm';
import ChipsForm from '@/components/component_form/ChipsForm';
import EmailForm from '@/components/component_form/email';
import ChipsFormIcon from '@/components/component_form/ChipsFormIcon';

// Slider (SSR enabled — no browser APIs during render)
import Slider from '@/components/component_unique/Slider';

// Cricket/Sports components
import SportsScroreCardHor from '@/components/component_astro_crick/SportsScroreCardHor';
import SportsScroreCardVert from '@/components/component_astro_crick/SportsScroreCardVert';
import IplWindRange from '@/components/component_astro_crick/IplWindRange';
import TeamPointsTable from '@/components/component_astro_crick/TeamPointsTable';
import CricketVoteLevel from '@/components/component_astro_crick/CricketVoteLevel';
import CricketQuestion from '@/components/component_astro_crick/CricketQuestion';
import TeamPlayersPointsTable from '@/components/component_astro_crick/TeamPlayersPointsTable';
import BowlerScoreTable from '@/components/component_astro_crick/BowlerScoreTable';
import CricketComentryOver from '@/components/component_astro_crick/CricketComentryOver';
import HorizontalCricketCommentryBallNews from '@/components/component_horizontal/HorizontalCricketCommentryBallNews';
import ScoreBoardWithTag from '@/components/component_astro_crick/ScoreBoardWithTag';

// Astrology components
import Astrology from '@/components/component_astro_crick/Astrology';
import AtrologyNewsImageAdd from '@/components/component_astro_crick/AtrologyNewsImageAdd';
import AstroNumbersGrid from '@/components/component_astro_crick/AstroNumbersGrid';
import AstrologyTab from '@/components/component_astro_crick/AstrologyTab';
import AstroQuestion from '@/components/component_astro_crick/AstroQuestion';
import AstroWithOneSideText from '@/components/component_astro_crick/AstroWithOneSideText';
import AstrologySection from '@/components/component_astro_crick/AstrologySection';

// Story/special components
import Story from '@/components/component_astro_crick/story';
import { VerticalStoryImageCategoryNews } from '@/components/component_vertical/VerticalStoryImageCategoryNews';
import { VerticalStoryImageDateNews } from '@/components/component_vertical/VerticalStoryImageDateNews';
import { VerticalBigStoryImageDateNews } from '@/components/component_vertical/VerticalBigStoryImageDateNews';
import { VerticalCategoryNewsSmall } from '@/components/component_vertical/VerticalCategoryNewsSmall';

// Profile/Auth components
import ProfileIconMenu from '@/components/component_unique/ProfileIconMenu';
import SubscribeDinamani from '@/components/component_unique/SubscribeDinamani';
import BuyDinamani from '@/components/component_unique/BuyDinamani';

// Primary home page components (first row)
import HorizontalBigImageCategoryNews from '@/components/component_horizontal/HorizontalBigImageCategoryNews';
import { VerticalMediamImageNews } from '@/components/component_vertical/VerticalMediamImageNews';
import HorizontalBigImageCategoryNewsHome from '@/components/component_horizontal/HorizontalBigImageCategoryNewsHome';
import TextWithImage from '@/components/component_unique/TextWithImage';
import { VerticalBigImagesNewsWhite } from '@/components/component_vertical/VerticalBigImagesNewsWhite';
import { VerticalMediumImageNewsBuilder } from '@/components/component_vertical/VerticalMediumImageNewsBuilder';
import VerticalGradientWithPlaybutton from '@/components/component_vertical/VerticalGradientWithPlaybutton';
import HorizontalBigVideoNews from '@/components/component_horizontal/HorizontalBigVideoNews';

// Vertical Components
import VerticalBigImagecategoryNews from '@/components/component_vertical/VerticalBigImagecategoryNews';
import { VerticalCategoryNews } from '@/components/component_vertical/VerticalCategoryNews';
import { VerticalMediamImageCategoryNews } from '@/components/component_vertical/VerticalMediamImageCategoryNews';
import { VerticalSmallImageCategoryNews } from '@/components/component_vertical/VerticalSmallImageCategoryNews';
import { VerticalMediamImageDescNews } from '@/components/component_vertical/VerticalMediamImageDescNews';
import { VerticalBigImageNews } from '@/components/component_vertical/VerticalBigImageNews';
import { VerticalBigImagesNews } from '@/components/component_vertical/VerticalBigImagesNews';
import { VerticalMediamImageDescNews2 } from '@/components/component_vertical/VerticalMediamImageDescNews2';
import { VerticalSmallImageNews } from '@/components/component_vertical/VerticalSmallImageNews';
import { VerticalSmallImagesNews } from '@/components/component_vertical/VerticalSmallImagesNews';
import VerticalheadingBigImageNews from '@/components/component_vertical/VerticalheadingBigImageNews';
import { VerticalMediamRatingNews } from '@/components/component_vertical/VerticalMediamRatingNews';
import { VerticalMediamTagNews } from '@/components/component_vertical/VerticalMediamTagNews';
import { VerticalMediumImageHeadingNews } from '@/components/component_vertical/VerticalMediumImageHeadingNews';
import VerticalBigImagecategoryNewsLive from '@/components/component_vertical/VerticalBigImagecategoryNewsLive';
import VerticalImageGradiantNews from '@/components/component_vertical/VerticalImageGradiantNews';
import { VerticalMediumDescNews } from '@/components/component_vertical/VerticalMediumDescNews';
import VerticalBigImagecategoryNewsbgWhite from '@/components/component_vertical/VerticalBigImagecategoryNewsbgWhite';
import { VerticalSmallImagesNewsWhite } from '@/components/component_vertical/VerticalSmallImagesNewsWhite';

// Horizontal Components
import HorizontalBigImageNewsWhite from '@/components/component_horizontal/HorizontalBigImageNewsWhite';
import HorizontalSmallImageCategoryNews from '@/components/component_horizontal/HorizontalSmallImageCategoryNews';
import HorizontalSmallRightImageCategoryNews from '@/components/component_horizontal/HorizontalSmallRightImageCategoryNews';
import HorizontalSmallCategoryNews from '@/components/component_horizontal/HorizontalSmallCategoryNews';
import HorizontalSmallNews from '@/components/component_horizontal/HorizontalSmallNews';
import HorizontalBigImageNews from '@/components/component_horizontal/HorizontalBigImageNews';
import HorizontalSmallImageHeadingNews from '@/components/component_horizontal/HorizontalSmallImageHeadingNews';
import HorizontalSideMostview from '@/components/component_horizontal/horizontalSideMostview';
import HorizontalCommentsNews from '@/components/component_horizontal/HorizontalCommentsNews';
import HorizontalCommentsIconsNews from '@/components/component_horizontal/HorizontalCommentsIconsNews';
import HorizontalDateNews from '@/components/component_horizontal/HorizontalDateNews';
import HorizontalSmallImageNews from '@/components/component_horizontal/HorizontalSmallImageNews';
import HorizontalBigImageAuthorNews from '@/components/component_horizontal/HorizontalBigImageAuthorNews';
import HorizontalBigCategorynews from '@/components/component_horizontal/HorizontalBigCategorynews';
import AuthorNameWithImage from '@/components/component_horizontal/AuthorNameWithImage';

// Unique Components
import FollowUsFacebook from '@/components/component_unique/FollowUsFacebook';
import EmojiRating from '@/components/component_unique/EmojiRating';
import Divider from '@/components/component_unique/divider';
import SocialMediaButtons from '@/components/component_unique/SocialMediaButtons';
import DistrictNameImage from '@/components/component_unique/DistrictNameImage';
import Searchbar from '@/components/component_unique/searchbar';
import SearchNoResult from '@/components/component_unique/SearchNoResult';
import FollowUsTwitter from '@/components/component_unique/FollowUsTwitter';
import NewsWidget from '@/components/component_unique/NewsWidget';
import PaperHeader from '@/components/component_unique/PaperHeader';

// Other components
import PollResult from '@/components/component_astro_crick/PollResult';
import SendComment from '@/components/component_astro_crick/SendComment';
import LatestCard from '@/components/component_astro_crick/LatestCard';
import IconWithtext from '@/components/component_astro_crick/IconWithtext';
import ArticleText2 from '@/components/component_astro_crick/ArticleText2';
import BigNewsWithicon from '@/components/component_astro_crick/BigNewsWithicon';
import OnesideText from '@/components/component_astro_crick/OnesideText';
import Pagination from '@/components/component_astro_crick/pagination';
import RecentNews from '@/components/component_astro_crick/RecentNews';
import TextWithBigImageAndIcon from '@/components/component_astro_crick/TextWithBigImageAndIcon';
import WhiteMenuBar from '@/components/component_astro_crick/WhiteMenuBar';
import WhiteMenuBar2 from '@/components/component_astro_crick/WhiteMenuBar2';
import ImageWithText from '@/components/ui/imagewithtext';
import ImageText from '@/components/component_vertical/imagetext';

// ====== CLIENT-ONLY COMPONENTS (ssr: false — browser APIs / heavy libs) ======

// Charts/maps using heavy libraries (Highcharts, Recharts, d3-geo)
const CircleChart = dynamic(() => import('@/components/component_astro_crick/CircleChart'), { ssr: false });
const IndiaMap = dynamic(() => import('@/components/component_astro_crick/IndiaMap'), { ssr: false });
const GaugeComparison = dynamic(() => import('@/components/component_astro_crick/GaugeComparison'), { ssr: false });

// Election components - only loaded on election pages
const Electiontable = dynamic(() => import('@/components/component_astro_crick/Electiontable'), { ssr: false });
const ElectionTableTwo = dynamic(() => import('@/components/component_astro_crick/ElectionTableTwo'), { ssr: false });
const TnResultCount = dynamic(() => import('@/components/component_astro_crick/TnResultCount'), { ssr: false });
const TnResultCountCandidate = dynamic(() => import('@/components/component_astro_crick/TnResultCountCandidate'), { ssr: false });

// Actual media players (init heavy iframe scripts on mount)
const VideoPlayer = dynamic(() => import('@/components/component_unique/VideoPlayer'), { ssr: false });
const AudioPlayer = dynamic(() => import('@/components/component_unique/AudioPlayer'), { ssr: false });
const PopupSlider = dynamic(() => import('@/components/component_unique/PopupSlider'), { ssr: false });
const VerticalNewsSlider = dynamic(() => import('@/components/component_astro_crick/VerticalNewsSlider'), { ssr: false });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const componentRegistry: Record<string, ComponentType<any>> = {
  // Vertical Components
  'VerticalBigImagecategoryNews': VerticalBigImagecategoryNews,
  'VerticalBigImageCategoryNews': VerticalBigImagecategoryNews, // alias with different casing
  'VerticalMediamImageNews': VerticalMediamImageNews,
  'VerticalMediumImageNews': VerticalMediamImageNews, // alias
  'VerticalCategoryNews': VerticalCategoryNews,
  'VerticalMediamImageCategoryNews': VerticalMediamImageCategoryNews,
  'VerticalMediumImageCategoryNews': VerticalMediamImageCategoryNews, // alias
  'VerticalSmallImageCategoryNews': VerticalSmallImageCategoryNews,
  'VerticalMediamImageDescNews': VerticalMediamImageDescNews,
  'VerticalMediumImageDescNews': VerticalMediamImageDescNews, // alias with correct spelling
  'VerticalMediumIDescNews': VerticalMediumDescNews, // alias from JSON
  'VerticalBigVideoNews': VerticalBigVideoNews,
  'VerticalBigVideoNewsBlack': VerticalBigVideoNewsBlack,
  'VerticalStoryImageCategoryNews': VerticalStoryImageCategoryNews,
  'VerticalBigImageNews': VerticalBigImageNews,
  'VerticalStoryImageDateNews': VerticalStoryImageDateNews,
  'VerticalBigStoryImageDateNews': VerticalBigStoryImageDateNews,
  'VerticalCategoryNewsSmall': VerticalCategoryNewsSmall,
  'VerticalBigImagesNews': VerticalBigImagesNews,
  'VerticalMediamImageDescNews2': VerticalMediamImageDescNews2,
  'VerticalSmallImageNews': VerticalSmallImageNews,
  'VerticalSmallImagesNews': VerticalSmallImagesNews,
  'VerticalheadingBigImageNews': VerticalheadingBigImageNews,
  'VerticalMediumVideoNews': VerticalMediumVideoNews,
  'VerticalBigVideoCategoryNews': VerticalBigVideoCategoryNews,
  'VerticalMediamRatingNews': VerticalMediamRatingNews,
  'VerticalMediamTagNews': VerticalMediamTagNews,
  'VerticalMediumVideoHeadingNews': VerticalMediumVideoHeadingNews,
  'VerticalMediumImageHeadingNews': VerticalMediumImageHeadingNews,
  'VerticalBigImagecategoryNewsLive': VerticalBigImagecategoryNewsLive,
  'VerticalBigImageCategoryNewsBG': VerticalBigImagecategoryNews, // alias for background variant
  'VerticalImageGradiantNews': VerticalImageGradiantNews,
  'VerticalBigImagecategoryNewsbgWhite': VerticalBigImagecategoryNewsbgWhite,
  'VerticalSmallImagesNewsWhite': VerticalSmallImagesNewsWhite,
  'VerticalBigImagesNewsWhite': VerticalBigImagesNewsWhite,
  'VerticalMediumImageNewsBuilder':VerticalMediumImageNewsBuilder,
  'VerticalGradientWithPlaybutton':VerticalGradientWithPlaybutton,
  // Horizontal Components
  'HorizontalBigImageCategoryNews': HorizontalBigImageCategoryNews,
  'HorizontalCategoryNews': HorizontalSmallCategoryNews, // alias for missing component
  'HorizontalSmallImageCategoryNews': HorizontalSmallImageCategoryNews,
  'HorizontalSmallRightImageCategoryNews': HorizontalSmallRightImageCategoryNews,
  'HorizontalSmallCategoryNews': HorizontalSmallCategoryNews,
  'HorizontalSmallNews': HorizontalSmallNews,
  'HorizontalBigImageNews': HorizontalBigImageNews,
  'HorizontalSmallImageHeadingNews': HorizontalSmallImageHeadingNews,
  'HorizontalBigVideoCategoryNews': HorizontalBigVideoCategoryNews,
  'HorizontalBigVideoCategoryNewsBlack': HorizontalBigVideoCategoryNewsBlack,
  'horizontalSideMostview': HorizontalSideMostview,
  'HorizontalCommentsNews': HorizontalCommentsNews,
  'HorizontalSmallVideoRightNews': HorizontalSmallVideoRightNews,
  'HorizontalCommentsIconsNews': HorizontalCommentsIconsNews,
  'HorizontalDateNews': HorizontalDateNews,
  'HorizontalVideoNews': HorizontalVideoNews,
  'HorizontalCricketCommentryBallNews': HorizontalCricketCommentryBallNews,
  'HorizontalSmallImageNews': HorizontalSmallImageNews,
  'HorizontalBigImageAuthorNews': HorizontalBigImageAuthorNews,
  'HorizontalBignews' : HorizontalBigCategorynews,
  'HorizontalBigCategorynews' : HorizontalBigCategorynews,
  'HorizontalBigImageCategoryNewsHome':HorizontalBigImageCategoryNewsHome,
  'HorizontalBigImageNewsWhite':HorizontalBigImageNewsWhite,
  'HorizontalSmallImageCategoryNewsWhite':HorizontalSmallImageCategoryNews,
  "HorizontalBigVideoNews" : HorizontalBigVideoNews,
  // Form Components
  'Form': Form,
  'CompanyForm': CompanyForm,
  'EditProfileForm': EditProfileForm,
  'NewsPaperfeedbackForm': NewsPaperfeedbackForm,
  'LoginRegisterForm': LoginRegisterForm,
  'OTPForm': OTPForm,
  'ReadFullForm': ReadFullForm,
  'PollForm': PollForm,
  'ChipsForm': ChipsForm,
  'EmailForm': EmailForm,
  'ChipsFormIcon': ChipsFormIcon,
  'AstrologySection': AstrologySection,
  // Unique Components
  'Slider': Slider,
  'FollowUsFacebook': FollowUsFacebook,
  'VideoPlayer': VideoPlayer,
  'AudioPlayer': AudioPlayer,
  'EmojiRating': EmojiRating,
  'Divider': Divider,
  'SocialMediaButtons': SocialMediaButtons,
  'DistrictNameImage': DistrictNameImage,
  'Searchbar': Searchbar,
  'SearchNoResult': SearchNoResult,
  'ProfileIconMenu': ProfileIconMenu,
  'SubscribeDinamani': SubscribeDinamani,
  'BuyDinamani': BuyDinamani,
  'PopupSlider': PopupSlider,
  'FollowUsTwitter': FollowUsTwitter,
  'BigVideonews': BigVideonews,
  'NewsWidget': NewsWidget,
  'PaperHeader': PaperHeader,
  'AuthorNameWithImage':AuthorNameWithImage,
  'ImageWithText':ImageWithText,
  'ImageText':ImageText,
  'videoPlayer':VideoPlayer,
  'TextWithImage':TextWithImage,

  // Astro/Cricket Components
  'SportsScroreCardHor': SportsScroreCardHor,
  'Astrology': Astrology,
  'astrologyWidget': Astrology, // alias for widget variant
  'AtrologyNewsImageAdd': AtrologyNewsImageAdd,
  'PollResult': PollResult,
  'SportsScroreCardVert': SportsScroreCardVert,
  'IplWindRange': IplWindRange,
  'TeamPointsTable': TeamPointsTable,
  'CricketVoteLevel': CricketVoteLevel,
  'CricketQuestion': CricketQuestion,
  'TeamPlayersPointsTable': TeamPlayersPointsTable,
  'BowlerScoreTable': BowlerScoreTable,
  'CricketComentryOver': CricketComentryOver,
  'SendComment': SendComment,
  'TnResultCount': TnResultCount,
  'Electiontable': Electiontable,
  'TnResultCountCandidate': TnResultCountCandidate,
  'CircleChart': CircleChart,
  'LatestCard': LatestCard,
  'IndiaMap': IndiaMap,
  'ElectionTableTwo': ElectionTableTwo,
  'GaugeComparison': GaugeComparison,
  'IconWithtext': IconWithtext,
  'AstroNumbersGrid': AstroNumbersGrid,
  'ArticleText2': ArticleText2,
  'BigNewsWithicon': BigNewsWithicon,
  'OnesideText': OnesideText,
  'Pagination': Pagination,
  'RecentNews': RecentNews,
  'Story': Story,
  'TextWithBigImageAndIcon': TextWithBigImageAndIcon,
  'WhiteMenuBar': WhiteMenuBar,
  'VerticalNewsSlider': VerticalNewsSlider,
  'WhiteMenuBar2': WhiteMenuBar2,
  'AstrologyTab': AstrologyTab,
  'AstroQuestion': AstroQuestion,
  'AstroWithOneSideText': AstroWithOneSideText,
  'ScoreBoardWithTag': ScoreBoardWithTag,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getComponent(componentType: string): ComponentType<any> | null {
  return componentRegistry[componentType] || null;
}
